import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  IconButton,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  Switch,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  EditOutlined as EditOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  RestoreOutlined as RestoreOutlinedIcon,
  People as PeopleIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../commons/generics/StyledTableComponents';
import { ModalConfirmDeactivation } from '../../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../../components/modals/ModalConfirmRestore';
import {
  getRulesAndAlertsPaginationStore,
  getRulesAndAlertsBySearchStore,
  deleteRulesAndAlertStore,
  restoreRulesAndAlertStore,
  getRulesAndAlertDetailStore,
} from '../../../../store/ratings/thunks';
import {
  corregirTexto,
  firstLetterUpperCase,
  hasPemissions,
  usarFuncionesComposicion,
} from '../../../../commons/utils';
import { useTableState } from '../../../../hooks/useTableState';
import { GenericTableHead, HeadCell } from '../../../../commons/generics/GenericTableHead';
import { SinDatos } from '../../../../commons/generics/SinDatos';
import { ClampedText } from '../../../../commons/generics/ClampedText';
import { StatusChip } from '../../../../commons/generics/StatusChip';

interface TableRulesProps {
  onEdit: (rule: any) => void;
  refreshTrigger: number;
}

export const TableRules: React.FC<TableRulesProps> = ({ onEdit, refreshTrigger }) => {
  const dispatch = useDispatch();

  // REDUX STATE
  const { rules_and_alerts_list } = useSelector((state: any) => state.ratingsDevices);

  // TABLE STATE
  const {
    dataTableList,
    canView,
    setCanView,
    canEdit,
    setCanEdit,
    canDelete,
    setCanDelete,
    canRestore,
    setCanRestore,
    openElem,
    setOpenElem,
    checked,
    setChecked,
    searchTerm,
    setSearchTerm,
    order,
    orderBy,
    handleSwitchChange,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    handleRequestSort,
    updatePaginationFromList,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    ResponseComponents,
    handleResponse,
    getSortedAndFilteredList,
    isServerSide,
  } = useTableState('rulesAndAlerts');

  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (rules_and_alerts_list) {
      updatePaginationFromList(rules_and_alerts_list);
    }
  }, [rules_and_alerts_list]);

  useEffect(() => {
    checkPermissions();
    getInitialData();
  }, []);

  useEffect(() => {
    if (hasPermission) {
      getInitialData();
    }
  }, [refreshTrigger]);

  const getInitialData = async (currentStatus?: number) => {
    const status = currentStatus !== undefined ? currentStatus : checked ? 0 : 1;
    const response = await dispatch(getRulesAndAlertsPaginationStore(1, status));
    await handleResponse(response, 'obtener reglas', false);
  };

  const handleSearch = async (param: string) => {
    setSearchTerm(param);
    setInputValue(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) => getRulesAndAlertsBySearchStore(p, 1, checked ? 0 : 1),
      getInitialData,
      null,
      'buscar reglas'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;
    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => getRulesAndAlertsBySearchStore(p, 1, status),
        () => getInitialData(status),
        null,
        'buscar reglas'
      );
    } else {
      getInitialData(status);
    }
  };

  const onResetSearch = () => {
    setInputValue('');
    handleResetSearchHook(() => getInitialData());
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);
    const response = await dispatch(
      getRulesAndAlertsPaginationStore(value, checked ? 0 : 1) as any
    );
    await handleResponse(response, 'listar reglas', false);
  };

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.reglas.alertas',
      'view'
    );
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.reglas.alertas',
      'edit'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.reglas.alertas',
      'delete'
    );
    if (del) setCanDelete(true);

    const rest = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.reglas.alertas',
      'restore'
    );
    if (rest) setCanRestore(true);
  };

  const handleView = async (rule: any) => {
    // Here you can add view logic if needed
  };

  const handleEditAction = async (rule: any) => {
    const response = await dispatch(getRulesAndAlertDetailStore(rule.id));
    await handleResponse(response, 'obtener detalle', false);
    if (response?.ok) {
      onEdit(response.data);
    }
  };

  const handleDelete = (rule: any) => {
    setOpenElem(rule);
    setOpenModalDeactivation(true);
  };

  const handleRestoreAction = (rule: any) => {
    setOpenElem(rule);
    setOpenModalRestore(true);
  };

  const confirmDeactivation = async () => {
    if (!openElem) return;
    const response = await dispatch(
      deleteRulesAndAlertStore(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'desactivar regla', true);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      restoreRulesAndAlertStore(openElem.id, inputValue, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'restaurar regla', true);
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">No tienes permisos para ver este contenido.</Typography>
      </Box>
    );
  }

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['name', 'description'],
    'status',
    1
  );

  const headCells: HeadCell[] = [
    { id: 'name', label: 'Nombre', sortable: true },
    { id: 'description', label: 'Descripción', sortable: true },
    { id: 'users', label: 'Usuarios asignados', sortable: false, align: 'center' },
    { id: 'status', label: 'Estado', sortable: true, align: 'center' },
    { id: 'actions', label: 'Acciones', sortable: false, align: 'center' },
  ];

  return (
    <Box sx={{ width: '100%', mt: 5 }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar regla..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivas (Reglas)
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <GenericTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {sortedList.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <SinDatos />
                  </StyledTableCell>
                </TableRow>
              ) : (
                sortedList
                  .slice(
                    isServerSide ? 0 : page * rowsPerPage,
                    isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row: any) => (
                    <StyledTableRow key={row.id} hover>
                      <StyledTableCell sx={{ fontWeight: 700, color: '#2e263d' }}>
                        <ClampedText
                          text={
                            row.name
                              ? usarFuncionesComposicion(
                                  corregirTexto,
                                  firstLetterUpperCase,
                                  row.name
                                )
                              : null
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          color: 'text.secondary',
                          maxWidth: 300,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <ClampedText
                          text={
                            row.description
                              ? usarFuncionesComposicion(
                                  corregirTexto,
                                  firstLetterUpperCase,
                                  row.description
                                )
                              : null
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            color: '#4A148C',
                            fontWeight: 600,
                          }}
                        >
                          <PeopleIcon sx={{ fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.users?.length || 0} usuarios
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <StatusChip status={row.status} />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                            <IconButton
                              size="small"
                              onClick={() => canView && handleView(row)}
                              disabled={!canView}
                              color="info"
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          <span
                            style={{
                              cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => canEdit && row.status === 1 && handleEditAction(row)}
                              disabled={!canEdit || row.status !== 1}
                              color="primary"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          {row.status === 1 ? (
                            <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canDelete && handleDelete(row)}
                                disabled={!canDelete}
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          ) : (
                            <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canRestore && handleRestoreAction(row)}
                                disabled={!canRestore}
                                color="success"
                              >
                                <RestoreOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              )}
            </TableBody>
          </Table>
          {dataTableList.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataTableList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página"
            />
          )}

          {dataTableList.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 1,
                padding: 2.5,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Pagination
                sx={{ justifyContent: 'center' }}
                color="primary"
                count={itemCountPagination}
                page={pagePagination}
                onChange={changePaginationPage}
                showFirstButton
                showLastButton
                shape="circular"
              />
            </Box>
          )}
        </TableContainer>
      </Card>

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivation}
        title="Desactivar Regla"
        description="¿Está seguro que desea desactivar esta regla? No se enviarán más alertas basadas en ella."
      />

      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        onConfirm={confirmRestore}
        title="Restaurar Regla"
        description="¿Está seguro que desea activar esta regla nuevamente?"
      />
    </Box>
  );
};
